/**
 * SNAKE-30: header identity line.
 *
 *   Default (no trial)        : "👤 Имя · N⭐"
 *   Trial active (free games) : "👤 Имя · K🎮 + N⭐"
 *
 * Pure formatter — name truncation at 16 chars with ellipsis per GDD spec.
 */

const MAX_NAME_LENGTH = 16

export interface IdentityLineInput {
  displayName: string
  balance: number
  freeGamesLeft: number
}

export interface IdentityLine {
  /** Combined text used for accessibility / tooltip. */
  text: string
  /** Sanitised display name (truncated to 16 chars with `…`). */
  name: string
  /** "47⭐" or — with trial — "3🎮 + 5⭐". Renderer concatenates "👤 {name} · {metric}". */
  metric: string
}

export function buildIdentityLine(input: IdentityLineInput): IdentityLine {
  const name = truncateName(input.displayName)
  const balance = safeNonNegativeInt(input.balance)
  const free = safeNonNegativeInt(input.freeGamesLeft)

  const metric = free > 0
    ? `${free}🎮 + ${balance}⭐`
    : `${balance}⭐`

  return {
    text: `👤 ${name} · ${metric}`,
    name,
    metric,
  }
}

function truncateName(raw: string | null | undefined): string {
  if (typeof raw !== 'string' || raw.trim().length === 0) return 'Player'
  const trimmed = raw.trim()
  if (trimmed.length <= MAX_NAME_LENGTH) return trimmed
  return `${trimmed.slice(0, MAX_NAME_LENGTH - 1)}…`
}

function safeNonNegativeInt(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0
  return Math.max(0, Math.floor(value))
}
